import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useToast } from '../../providers/ToastProvider';
import { useUser } from '../../providers/UserProvider';
import { markWhatsNewSeen } from '../../services/user';
import { CURRENT_RELEASE } from './releases/currentRelease';
import { recordAdminSeen } from './seenRelease';
import { WhatsNewContext, type WhatsNewControls } from './whatsNewContext';
import {
    closedWhatsNew,
    INITIAL_WHATS_NEW_STATE,
    isWhatsNewOpen,
    openedWhatsNew
} from './whatsNewState';

const SAVE_FAILED_MESSAGE =
    "Couldn't save that you've seen What's new; it may show again next time.";

interface WhatsNewProviderProps {
    children: ReactNode;
}

// Lives above the routes because ProtectedRoute remounts on every navigation.
export function WhatsNewProvider({ children }: WhatsNewProviderProps) {
    const { user, updateLastSeenReleases } = useUser();
    const { showToast } = useToast();
    const [state, setState] = useState(INITIAL_WHATS_NEW_STATE);

    const userId = user?.id ?? null;
    const releases = user?.database.data.lastSeenReleases;
    const isOpen = isWhatsNewOpen(state, { userId, releases, release: CURRENT_RELEASE });

    const openWhatsNew = useCallback(() => {
        if (userId) setState((current) => openedWhatsNew(current, userId));
    }, [userId]);

    const closeWhatsNew = useCallback(async () => {
        if (!userId || !releases) return;
        setState((current) => closedWhatsNew(current, userId));
        try {
            const update = await recordAdminSeen(releases, CURRENT_RELEASE, markWhatsNewSeen);
            if (update) updateLastSeenReleases(update);
        } catch {
            showToast(SAVE_FAILED_MESSAGE, 'error');
        }
    }, [userId, releases, updateLastSeenReleases, showToast]);

    const value = useMemo<WhatsNewControls>(
        () => ({ isOpen, openWhatsNew, closeWhatsNew }),
        [isOpen, openWhatsNew, closeWhatsNew]
    );

    return <WhatsNewContext.Provider value={value}>{children}</WhatsNewContext.Provider>;
}
